'use client';

import { useMemo, useState } from 'react';
import Image from 'next/image';
import { useParams } from 'next/navigation';
import { useTranslation } from 'react-i18next';
import { blogs as baseBlogs } from '../blogs/blogs';
import { LangLink } from '../../../i18n/links';
import { defaultLang, SUPPORTED } from '../../../config/languages';

const stripHtml = (value = '') => value.replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim();

const getReadingTime = (blog, translation) => {
  const content = typeof translation.content === 'string' ? stripHtml(translation.content) : translation.description || '';
  return Math.max(3, Math.ceil(content.split(/\s+/).filter(Boolean).length / 180));
};

const getCategory = (blog) => {
  const tags = (blog.tags || []).join(' ').toLowerCase();
  if (/canva|template|design/.test(tags)) return 'product';
  if (/restaurant|business|marketing|expo/.test(tags)) return 'business';
  if (/guide|android|fire tv|player/.test(tags)) return 'guides';
  return 'inspiration';
};

const getSlug = (blog, language) => {
  if (typeof blog.slug === 'string') return blog.slug;
  return blog.slug?.[language] || blog.slug?.en || '';
};

function ArticleMeta({ blog, category, readingTime, t }) {
  return (
    <div className="blog-v2-meta">
      <span>{readingTime} {t('blog:layout2.minuteRead')}</span>
      <span aria-hidden="true">•</span>
      <span>{t(`blog:layout2.categories.${category}`)}</span>
    </div>
  );
}

function FeaturedArticle({ article, t }) {
  return (
    <article className="blog-v2-featured">
      <div className="blog-v2-featured-copy">
        <span className="blog-v2-featured-label">{t('blog:layout2.featured')}</span>
        <ArticleMeta {...article} t={t} />
        <h2><LangLink to={`/blog/${article.slug}`}>{article.title}</LangLink></h2>
        <p>{article.description}</p>
        <LangLink className="blog-v2-read-link" to={`/blog/${article.slug}`}>
          {t('blog:layout2.readStory')} <span aria-hidden="true">→</span>
        </LangLink>
      </div>
      <LangLink className="blog-v2-featured-image" to={`/blog/${article.slug}`} aria-label={article.title}>
        <Image src={article.image} alt={article.imageAlt || article.title} fill priority sizes="(max-width: 900px) 100vw, 58vw" />
      </LangLink>
    </article>
  );
}

function ArticleCard({ article, t }) {
  return (
    <article className="blog-v2-card">
      <LangLink className="blog-v2-card-image" to={`/blog/${article.slug}`} aria-label={article.title}>
        <Image src={article.image} alt={article.imageAlt || article.title} fill sizes="(max-width: 767px) 100vw, 33vw" />
      </LangLink>
      <ArticleMeta {...article} t={t} />
      <h2><LangLink to={`/blog/${article.slug}`}>{article.title}</LangLink></h2>
      <p>{article.description}</p>
      <LangLink className="blog-v2-read-link" to={`/blog/${article.slug}`}>
        {t('blog:readMore')} <span aria-hidden="true">→</span>
      </LangLink>
    </article>
  );
}

export default function BlogLayout2() {
  const { t } = useTranslation(['blog']);
  const { lang = defaultLang } = useParams();
  const activeLang = SUPPORTED.includes(lang) ? lang : defaultLang;
  const [activeCategory, setActiveCategory] = useState('all');

  const articles = useMemo(() => baseBlogs.map((blog) => {
    const translation = blog.translations?.[activeLang] || blog.translations?.en || blog;
    return {
      ...blog,
      title: translation.title || '',
      description: translation.description || '',
      slug: getSlug(blog, activeLang),
      category: getCategory(blog),
      readingTime: getReadingTime(blog, translation),
    };
  }), [activeLang]);

  const [featured, ...remaining] = articles;
  const visibleArticles = activeCategory === 'all'
    ? remaining
    : remaining.filter((article) => article.category === activeCategory);
  const categories = ['all', 'guides', 'inspiration', 'product', 'business'];

  return (
    <>
      <link rel="stylesheet" href="/assets/css/blog-layout-2.css" />
      <main className="blog-v2-page">
        <section className="blog-v2-hero">
          <div className="container blog-v2-container">
            <p className="blog-v2-eyebrow">{t('blog:layout2.eyebrow')}</p>
            <h1>{t('blog:layout2.title')}</h1>
            <p className="blog-v2-intro">{t('blog:layout2.subtitle')}</p>
            {featured && <FeaturedArticle article={featured} t={t} />}
          </div>
        </section>

        <section className="blog-v2-articles" aria-labelledby="blog-v2-latest-title">
          <div className="container blog-v2-container">
            <div className="blog-v2-section-heading">
              <div>
                <p className="blog-v2-eyebrow">{t('blog:layout2.exploreEyebrow')}</p>
                <h2 id="blog-v2-latest-title">{t('blog:layout2.latest')}</h2>
              </div>
              <div className="blog-v2-categories" role="group" aria-label={t('blog:layout2.filterLabel')}>
                {categories.map((category) => (
                  <button
                    key={category}
                    type="button"
                    className={activeCategory === category ? 'is-active' : ''}
                    aria-pressed={activeCategory === category}
                    onClick={() => setActiveCategory(category)}
                  >
                    {t(`blog:layout2.categories.${category}`)}
                  </button>
                ))}
              </div>
            </div>

            <div className="blog-v2-grid">
              {visibleArticles.map((article) => <ArticleCard key={article.id} article={article} t={t} />)}
            </div>
            {visibleArticles.length === 0 && <p className="blog-v2-empty">{t('blog:layout2.noArticles')}</p>}
          </div>
        </section>
      </main>
    </>
  );
}
